import { useEffect, useState } from "react";
import clsx from "clsx";
import { useCampaignRunStore } from "../store/useCampaignRunStore";
import { useGameStore } from "../store/useGameStore";
import { CampaignMap } from "../components/CampaignMap";
import { BattleArena } from "../components/BattleArena";
import { ResultOverlay } from "../components/ResultOverlay";
import { CampaignBalanceModal } from "../components/CampaignBalanceModal";
import { AutoBattleModeModal } from "../components/AutoBattleModeModal";
import { FormattedNumber } from "../components/FormattedNumber";
import { useBattleReplay } from "../hooks/useBattleReplay";
import { isCampaignFightNode } from "../constants/autoBattle";
import { resolveAutoBattlePrefs, resolveAutoBattleEntitlements, isAutoBattleModeAllowed } from "../utils/autoBattle";
import type { AutoBattleMode, CampaignMoveResult } from "../types";

function isCampaignFightDisplayType(displayType: string) {
  return (
    displayType === "battle" ||
    displayType === "elite" ||
    displayType === "final_boss" ||
    displayType === "boss"
  );
}

export function CampaignRunScreen() {
  const {
    activeStage,
    run,
    lastMoveResult,
    nodeBattleResult,
    isLoading,
    error,
    moveToNode,
    finishNodeBattle,
    finishNodeBattleAfterBalance,
    reset,
    loadRun,
    abandonRun,
  } = useCampaignRunStore();
  const { hero, setScreen } = useGameStore();
  const [pendingNodeId, setPendingNodeId] = useState<string | null>(null);
  const [pickerMode, setPickerMode] = useState<AutoBattleMode>("rotation");
  const [exitConfirm, setExitConfirm] = useState(false);

  const autoBattlePrefs = resolveAutoBattlePrefs(hero);
  const autoBattleEntitlements = resolveAutoBattleEntitlements(hero);

  const handleSelectNode = (nodeId: string) => {
    if (!run || isLoading) return;
    const node = run.nodes.find((n) => n.id === nodeId);
    if (!node?.reachable) return;

    const fightNode = isCampaignFightNode(node.display_type);
    if (fightNode && autoBattlePrefs.prompt_on_start !== false) {
      setPickerMode(autoBattlePrefs.mode);
      setPendingNodeId(nodeId);
      return;
    }
    void moveToNode(nodeId, fightNode ? autoBattlePrefs.mode : undefined);
  };

  useEffect(() => {
    if (activeStage && !run) {
      void loadRun(activeStage.id);
    }
  }, [activeStage?.id, loadRun, run]);

  if (nodeBattleResult?.log) {
    return (
      <CampaignNodeBattle
        key={`${run?.id ?? "run"}-${nodeBattleResult.node_id}`}
        result={nodeBattleResult}
        log={nodeBattleResult.log}
        runId={nodeBattleResult.battle_balance?.run_id ?? run?.id ?? 0}
        battleContextLabel={run?.stage_name ?? activeStage?.name ?? "Campaign"}
        heroClass={hero?.hero_class}
        heroName={hero?.name}
        fallbackMaxHp={hero?.stats.hp ?? 1000}
        onContinue={finishNodeBattle}
        onContinueAfterBalance={finishNodeBattleAfterBalance}
      />
    );
  }

  const isDone = run?.status === "COMPLETED" || run?.status === "FAILED";

  const handleExitRun = () => {
    if (!exitConfirm) {
      setExitConfirm(true);
      return;
    }
    void abandonRun().then(() => {
      setExitConfirm(false);
      setScreen("campaign");
    });
  };

  return (
    <>
    <div className="p-4 space-y-4 pb-8">
      <div className="text-center px-2">
        <h2 className="text-xl font-bold font-game text-gold-400 truncate">
          {run?.stage_name ?? activeStage?.name ?? "Campaign"}
        </h2>
        <p className="text-[11px] text-gray-500 mt-1">Hero busy — other battles unavailable</p>
      </div>

      <p className="text-gray-400 text-center text-xs">
        Path to the 👑 Chapter Boss. ⚔️ fights pack more enemies deeper in; 💀 elite is one strong foe. ❓ is a surprise.
      </p>

      {isLoading && !run && (
        <div className="text-center py-12 text-gray-400 animate-pulse">Loading map...</div>
      )}

      {run && !isDone && (
        <div className="bg-kingdom-purple rounded-xl border border-kingdom-mid p-3 space-y-3">
          <div className="flex items-center justify-between text-sm gap-2">
            <span className="text-white font-semibold">Branching path</span>
            <span className="text-gray-400 shrink-0">
              {run.nodes.filter((n) => n.cleared).length}/{run.nodes.length} cleared
            </span>
          </div>
          <CampaignMap
            nodes={run.nodes}
            edges={run.edges}
            onSelectNode={handleSelectNode}
            isLoading={isLoading}
          />
          <button
            type="button"
            onClick={handleExitRun}
            className={clsx(
              "w-full py-2 rounded-lg text-sm font-semibold border",
              exitConfirm
                ? "border-rose-500 bg-rose-900/40 text-rose-200"
                : "border-gray-600 text-gray-400 hover:text-gray-200",
            )}
          >
            {exitConfirm ? "Confirm exit — progress on this run is lost" : "Exit run"}
          </button>
        </div>
      )}

      {lastMoveResult && (
        <OutcomeModal
          result={lastMoveResult}
          onClose={() => useCampaignRunStore.setState({ lastMoveResult: null })}
        />
      )}

      {run && isDone && (
        <div
          className={clsx(
            "rounded-xl p-6 text-center border-2 space-y-4",
            run.status === "COMPLETED"
              ? "bg-emerald-900/30 border-emerald-500"
              : "bg-rose-900/30 border-rose-500",
          )}
        >
          <div className="text-5xl">{run.status === "COMPLETED" ? "🏆" : "💀"}</div>
          <div className="text-xl font-bold font-game text-white">
            {run.status === "COMPLETED" ? "Stage Cleared!" : "Run ended"}
          </div>
          <button
            onClick={() => {
              reset();
              setScreen("campaign");
            }}
            className="px-8 py-3 bg-gradient-to-r from-kingdom-accent to-purple-700 text-white font-bold rounded-xl"
          >
            Back to Campaign
          </button>
        </div>
      )}

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">
          {error}
        </div>
      )}
    </div>
    {pendingNodeId && (
      <AutoBattleModeModal
        hero={hero}
        modeEntitlements={autoBattleEntitlements}
        onNeedPremium={() => setScreen("premium")}
        selected={pickerMode}
        onClose={() => setPendingNodeId(null)}
        onSelect={(mode) => {
          if (!isAutoBattleModeAllowed(mode, autoBattleEntitlements)) return;
          const nodeId = pendingNodeId;
          setPendingNodeId(null);
          void moveToNode(nodeId, mode);
        }}
      />
    )}
    </>
  );
}

function OutcomeModal({
  result,
  onClose,
}: {
  result: CampaignMoveResult;
  onClose: () => void;
}) {
  const isBad =
    result.display_type === "dead_end" ||
    result.revealed_type === "dead_end" ||
    result.battle_result === "LOSS";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div
        className={clsx(
          "w-full max-w-sm rounded-2xl border-2 p-5 space-y-3 animate-bounce-in",
          isBad ? "bg-rose-950/90 border-rose-500" : "bg-kingdom-purple border-kingdom-glow",
        )}
      >
        <div className="text-4xl text-center">
          {result.revealed_type === "treasure" || result.display_type === "treasure"
            ? "💰"
            : result.revealed_type === "dead_end" || result.display_type === "dead_end"
              ? "🚫"
              : result.revealed_type === "battle" || result.battle_result === "WIN"
                ? "⚔️"
                : result.revealed_type === "elite"
                  ? "💀"
                  : result.display_type === "final_boss"
                    ? "👑"
                    : "❓"}
        </div>
        {result.revealed_type && (
          <p className="text-[11px] text-center text-gray-400">
            Revealed:{" "}
            {result.revealed_type === "dead_end"
              ? "trap"
              : result.revealed_type === "battle"
                ? "ambush fight"
                : result.revealed_type}
          </p>
        )}
        <p className="text-white text-center text-sm font-medium">{result.outcome_message}</p>
        {result.rewards && (
          <div className="flex justify-center gap-4 text-sm">
            {result.rewards.gold > 0 && (
              <span className="text-gold-400">🪙 +<FormattedNumber value={result.rewards.gold} /></span>
            )}
            {result.rewards.xp > 0 && (
              <span className="text-blue-400">⭐ +<FormattedNumber value={result.rewards.xp} /> XP</span>
            )}
          </div>
        )}
        {result.energy_lost > 0 && (
          <p className="text-rose-300 text-center text-xs">⚡ −{result.energy_lost} energy</p>
        )}
        {result.debuff_applied?.message && (
          <p className="text-amber-300 text-center text-xs">{result.debuff_applied.message}</p>
        )}
        {result.stage_completed && (
          <p className="text-emerald-300 text-center text-xs font-bold">Chapter stage complete!</p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full py-2.5 rounded-xl bg-kingdom-mid text-white font-semibold"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function CampaignNodeBattle({
  result,
  log,
  runId,
  battleContextLabel,
  heroClass,
  heroName,
  fallbackMaxHp,
  onContinue,
  onContinueAfterBalance,
}: {
  result: CampaignMoveResult;
  log: NonNullable<CampaignMoveResult["log"]>;
  runId: number;
  battleContextLabel?: string;
  heroClass: import("../types").HeroClass | undefined;
  heroName?: string;
  fallbackMaxHp: number;
  onContinue: () => void;
  onContinueAfterBalance: (run: import("../types").CampaignRun) => Promise<void>;
}) {
  const { visibleLog, done } = useBattleReplay(log);
  const heroSnapshot = useGameStore((s) => s.heroSnapshot);
  const hero = useGameStore((s) => s.hero);
  const user = useGameStore((s) => s.user);
  const adminCampaignBalanceMode = useGameStore((s) => s.adminCampaignBalanceMode);
  const pushInfoMessages = useGameStore((s) => s.pushInfoMessages);
  const plannedRotation = heroSnapshot?.rotation ?? hero?.rotation;
  const isWin = result.battle_result === "WIN";
  const isFinalBoss = result.display_type === "final_boss" || result.display_type === "boss";
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [pendingContinue, setPendingContinue] = useState(false);

  const shouldOfferBalance =
    Boolean(user?.is_admin) &&
    adminCampaignBalanceMode &&
    result.battle_balance &&
    isCampaignFightDisplayType(result.display_type);

  useEffect(() => {
    useCampaignRunStore.getState().setDebugReplay(visibleLog, done);
  }, [visibleLog, done]);

  const finishAfterBalance = () => {
    setShowBalanceModal(false);
    setPendingContinue(false);
    onContinue();
  };

  const handleResultContinue = () => {
    if (shouldOfferBalance) {
      setShowBalanceModal(true);
      setPendingContinue(true);
      return;
    }
    onContinue();
  };

  const handleBalanceDone = (outcome: { kind: "skip" } | { kind: "applied"; message: string; run: import("../types").CampaignRun }) => {
    if (outcome.kind === "applied") {
      pushInfoMessages([outcome.message]);
      setShowBalanceModal(false);
      setPendingContinue(false);
      void onContinueAfterBalance(outcome.run);
      return;
    }
    finishAfterBalance();
  };

  return (
    <div className="relative h-full min-h-0">
      <BattleArena
        visibleLog={visibleLog}
        fullBattleLog={log}
        plannedRotation={plannedRotation}
        heroClass={heroClass}
        heroName={heroName}
        fallbackMaxHp={fallbackMaxHp}
        battleContextLabel={battleContextLabel}
      />
      {done && !showBalanceModal && (
        <ResultOverlay
          isWin={isWin}
          icon={isWin && isFinalBoss ? "👑" : undefined}
          title={
            isWin
              ? isFinalBoss
                ? "Chapter Boss Defeated!"
                : result.display_type === "elite"
                  ? "Elite Defeated!"
                  : "Victory!"
              : "Defeated — try this node again"
          }
          rewards={result.rewards}
          onContinue={handleResultContinue}
        />
      )}
      {showBalanceModal && result.battle_balance && pendingContinue && runId > 0 && (
        <CampaignBalanceModal
          balance={result.battle_balance}
          runId={runId}
          onDone={handleBalanceDone}
        />
      )}
    </div>
  );
}
